import React, { useEffect } from "react";
import Dialog from "../base/ui/components/web/Dialog";
import Container from "../base/react/components/web/Container";
import { useDispatch, useSelector } from "react-redux";
import { getAge, getHabits, getFileURL } from "./funtions";
import { shareBlobImage } from "../collab-images/actions";
import DialogWithTabs from "../base/ui/components/web/DialogWithTabs";
import { IconCalendar, IconExclamationTriangle, IconOdontoConectaSmall, IconShareDoc } from "../base/icons/svg";
import { useButtonStyles } from "../base/ui/components/web/Button";
import { useStyles } from "tss-react/mui";
import { setData } from "./actions";
/* -----------------------------------------------------------
   Utilities
----------------------------------------------------------- */
const isImageFile = (name) => [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp", ".svg"]
    .some(ext => name.toLowerCase().endsWith(ext));
/* -----------------------------------------------------------
   MAIN DIALOG
----------------------------------------------------------- */
export function OdontoConectaDialog() {
    const dispatch = useDispatch();
    const tegoServer = useSelector((s) => s["features/tego-server"]);
    const odontoconecta = useSelector((s) => s["features/odontoconecta"]);
    const collabImages = useSelector((s) => s["features/collab-images"]);
    const { classes: buttonClasses, cx } = useButtonStyles();
    if (!odontoconecta.fetched || !tegoServer.isEnabled) {
        return (React.createElement(Dialog, { title: "OdontoConecta" },
            React.createElement(Container, null,
                React.createElement(NoData, { text: "Sin datos" }))));
    }
    const { appointment, appointment: { patient } } = odontoconecta;
    const handleImageClick = async (fileUrl, fileName) => {
        if (!isImageFile(fileName) || !collabImages.excalidrawApi)
            return;
        const blob = await (await fetch(fileUrl, { mode: "cors", credentials: "include" })).blob();
        dispatch(shareBlobImage(blob, collabImages.excalidrawApi));
    };
    /* ========= ALERGIAS ========== */
    const allergies = patient.anamnesis?.[0];
    const hasAllergies = allergies &&
        (allergies.medicine_allergy ||
            allergies.other_allergy ||
            allergies.dental_material_allergy);
    /* ========= HÁBITOS ========== */
    const habits = patient.habit?.[0];
    const habitsText = habits ? getHabits(habits) : "";
    const theme = useStyles().theme;
    /* ========= ALERTAS ========== */
    const alerts = odontoconecta.appointment.more.alerts.data;
    const hasVitalSigns = alerts.vitalSigns && alerts.vitalSigns.length > 0;
    const hasDiseases = alerts.diseases && alerts.diseases.length > 0;
    const hasAlerts = hasVitalSigns || hasDiseases;
    /* -----------------------------------------------------------
       TABS
    ----------------------------------------------------------- */
    const tabs = [
        {
            name: "info",
            labelKey: "Información",
            icon: IconOdontoConectaSmall,
            component: () => {
                const sections = [
                    React.createElement(LabeledSection, { title: "Paciente", action: React.createElement("a", { href: `${tegoServer.urls.ui}clinical-record/${patient.id}`, target: "_blank", className: cx(buttonClasses.button, buttonClasses.small, buttonClasses['odonto-conecta']), style: { textDecoration: "none" } }, "Ver ficha"), items: [
                            { label: "Nombre", value: `${patient.names} ${patient.lastnames}` },
                            { label: "Edad", value: `${getAge(patient.birhtday)} años` }
                        ] }),
                    React.createElement(LabeledSection, { title: "Cita", items: [
                            { label: "Fecha", value: new Date(appointment.start).toLocaleString() },
                            { label: "Motivo", value: appointment.description || "Sin descripción" }
                        ] }),
                    hasAllergies && (React.createElement(LabeledSection, { title: "Alergias", items: [
                            allergies.medicine_allergy && {
                                label: "Medicamentos",
                                value: allergies.medicine_allergy
                            },
                            allergies.other_allergy && {
                                label: "Otros",
                                value: allergies.other_allergy
                            },
                            allergies.dental_material_allergy && {
                                label: "Material dental",
                                value: allergies.dental_material_allergy
                            }
                        ].filter(Boolean) })),
                    habitsText && (React.createElement(LabeledSection, { title: "H\u00E1bitos", items: [
                            {
                                label: "Listado",
                                value: (React.createElement("div", { style: {
                                        color: "rgba(255,255,255,0.95)",
                                        lineHeight: 1.4
                                    } }, habitsText))
                            }
                        ] })),
                ].filter(Boolean);
                return React.createElement(SectionList, { sections: sections, theme: theme });
            }
        },
        /* -----------------------------------------------------------
           TAB: ALERTAS
        ----------------------------------------------------------- */
        {
            name: "alerts",
            labelKey: "Alertas",
            unread: !odontoconecta.alertsReaded && hasAlerts,
            icon: IconExclamationTriangle,
            component: () => {
                const sections = [];
                useEffect(() => {
                    if (!odontoconecta.alertsReaded) {
                        dispatch(setData({ ...odontoconecta, alertsReaded: true }));
                    }
                }, []);
                if (hasVitalSigns) {
                    sections.push(React.createElement(LabeledSection, { title: "Signos vitales", items: alerts.vitalSigns.map(({ label, value }) => ({
                            label,
                            value
                        })) }));
                }
                if (hasDiseases) {
                    sections.push(React.createElement(ListSection, { title: "Enfermedades sin tratar", values: alerts.diseases }));
                }
                if (sections.length === 0) {
                    return React.createElement(NoData, { text: "No hay alertas." });
                }
                return React.createElement(SectionList, { sections: sections, theme: theme });
            }
        },
        /* -----------------------------------------------------------
           TAB: TRATAMIENTOS (MEJORADA)
        ----------------------------------------------------------- */
        {
            name: "treatments",
            labelKey: "Tratamientos",
            icon: IconCalendar,
            component: () => {
                const treatmentPlans = patient.treatment_plan;
                if (!treatmentPlans || treatmentPlans.length === 0) {
                    return React.createElement(NoData, { text: "No hay tratamientos registrados" });
                }
                const plan = treatmentPlans[0];
                return (React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: "0.8rem" } }, plan.treatment.map((t, i) => {
                    const createdAt = t.created_at
                        ? new Date(t.created_at).toLocaleString()
                        : "Sin fecha";
                    return (React.createElement("div", { key: i, style: {
                            padding: "1rem",
                            borderRadius: 8,
                            background: "rgba(255,255,255,0.06)",
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.6rem",
                            position: "relative"
                        } },
                        React.createElement("div", { style: {
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "start"
                            } },
                            React.createElement("div", { style: { fontSize: "1.1rem", fontWeight: 600 } }, t.procedure.benefit),
                            React.createElement("div", { style: { fontSize: "0.85rem", opacity: 0.55 } }, createdAt.split(",")[0])),
                        React.createElement("div", { style: { opacity: 0.9 } },
                            React.createElement("strong", null, "Ubicaci\u00F3n:"),
                            " ",
                            t.treatment_location.name),
                        React.createElement("div", { style: { opacity: 0.9 } },
                            React.createElement("strong", null, "Progreso:"),
                            " ",
                            t.progress),
                        t.observations && (React.createElement("div", { style: { opacity: 0.9 } },
                            React.createElement("strong", null, "Obs:"),
                            " ",
                            t.observations))));
                })));
            }
        },
        /* -----------------------------------------------------------
           TAB: DOCUMENTOS
        ----------------------------------------------------------- */
        {
            name: "documents",
            labelKey: "Documentos",
            icon: IconShareDoc,
            component: () => patient.complementary_exams?.length ? (React.createElement("div", { style: {
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: "12px",
                    paddingTop: "0.5rem"
                } }, patient.complementary_exams.map(exam => (React.createElement(DocumentCard, { key: exam.id, exam: exam, backendUrl: tegoServer.urls.odonto, onImageClick: handleImageClick }))))) : (React.createElement(NoData, { text: "No hay documentos disponibles" }))
        }
    ];
    return (React.createElement(DialogWithTabs, { hideSubmit: true, titleKey: "OdontoConecta", tabs: tabs }));
}
/* -----------------------------------------------------------
   SECTION LIST
----------------------------------------------------------- */
const SectionList = ({ sections, theme }) => (React.createElement("div", { style: { display: 'flex', flexDirection: 'column', gap: '0.6rem' } }, sections.map((section, i) => (React.createElement(React.Fragment, { key: i },
    section,
    i < sections.length - 1 && (React.createElement("div", { style: {
            borderBottom: `1px solid ${theme.palette.ui03}`,
            width: "100%"
        } })))))));
const LabeledSection = ({ title, items, action }) => (React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: "0.6rem" } },
    React.createElement("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center" } },
        React.createElement("div", { style: { fontSize: "1.05rem", fontWeight: 600 } }, title),
        action),
    React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: "0.35rem" } }, items.map((item, i) => (React.createElement("div", { key: i, style: { display: "flex", gap: "0.4rem" } },
        React.createElement("span", { style: { fontWeight: 600, color: "rgba(255,255,255,0.85)", minWidth: 80 } },
            item.label,
            ":"),
        React.createElement("span", { style: { color: "rgba(255,255,255,0.95)" } }, item.value)))))));
/* -----------------------------------------------------------
   LIST SECTION
----------------------------------------------------------- */
const ListSection = ({ title, values }) => (React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: "0.6rem" } },
    React.createElement("div", { style: { fontSize: "1.05rem", fontWeight: 600 } }, title),
    React.createElement("ul", { style: { margin: 0, paddingLeft: "1rem", color: "rgba(255,255,255,0.95)" } }, values.map((v, i) => (React.createElement("li", { key: i }, v))))));
/* -----------------------------------------------------------
   NODATA
----------------------------------------------------------- */
const NoData = ({ text }) => (React.createElement("div", { style: {
        textAlign: "center",
        padding: "2rem",
        color: "rgba(255,255,255,0.6)",
        fontStyle: "italic"
    } }, text));
function DocumentCard({ exam, backendUrl, onImageClick }) {
    const fileUrl = getFileURL(exam, backendUrl);
    const isImage = isImageFile(exam.file.name);
    const extension = exam.file.name.split(".").pop()?.toUpperCase() || "FILE";
    const wrapperStyle = {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "0.4rem",
        textDecoration: "none",
        background: "transparent",
        border: "none",
        padding: 0,
        cursor: "pointer",
        color: "rgba(255,255,255,0.95)"
    };
    const squareContainer = {
        width: "100%",
        aspectRatio: "1/1",
        position: "relative",
        overflow: "hidden",
        borderRadius: 6,
        background: "rgba(255,255,255,0.05)"
    };
    const imgStyle = {
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover"
    };
    const extensionStyle = {
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 500,
        fontSize: "1rem",
        color: "rgba(255,255,255,0.6)"
    };
    const fileNameStyle = {
        fontSize: "0.75rem",
        lineHeight: "1.15rem",
        wordBreak: "break-word",
        width: "100%"
    };
    if (isImage) {
        return (React.createElement("button", { style: wrapperStyle, onClick: () => onImageClick(fileUrl, exam.file.name) },
            React.createElement("div", { style: squareContainer },
                React.createElement("img", { src: fileUrl, alt: exam.file.name, style: imgStyle })),
            React.createElement("div", { style: fileNameStyle }, exam.file.name)));
    }
    return (React.createElement("a", { href: fileUrl, download: exam.file.name, target: "_blank", style: wrapperStyle },
        React.createElement("div", { style: squareContainer },
            React.createElement("div", { style: extensionStyle }, extension)),
        React.createElement("div", { style: fileNameStyle }, exam.file.name)));
}
// <Dialog size="large" title="OdontoConecta" ok={{hidden: true}}>
//     <Container style={styles.wrapper}>
//         {/* Navegación por tabs */}
//         {odontoconecta.appointment.description}
//         <div style={styles.tabContainer}>
//             <button
//                 style={{
//                     ...styles.tab,
//                     ...(activeTab === "informacion" ? styles.tabActive : {}),
//                 }}
//                 onClick={() => setActiveTab("informacion")}
//             >
//                 Información
//             </button>
//             <button
//                 style={{
//                     ...styles.tab,
//                     ...(activeTab === "documentos" ? styles.tabActive : {}),
//                 }}
//                 onClick={() => setActiveTab("documentos")}
//             >
//                 Documentos
//             </button>
//         </div>
//         {/* Contenido de Información */}
//         {activeTab === "informacion" && (
//             <>
//                 {/* Información personal */}
//                 <div style={styles.section}>
//                     <div style={{display: 'flex', justifyContent: 'space-between'}}>
//                         <div style={styles.sectionTitle}>Paciente</div>
//                         <a
//                             href={`${tegoServer.urls.ui}clinical-record/${patient.id}`}
//                             target="_blank"
//                             style={styles.button}
//                         >
//                             Ver ficha
//                         </a>
//                     </div>
//                     <div>
//                         <span style={styles.label}>Nombre:</span>{" "}
//                         <span style={styles.value}>
//                             {patient.names} {patient.lastnames}
//                         </span>
//                     </div>
//                     <div>
//                         <span style={styles.label}>Edad:</span>{" "}
//                         <span style={styles.value}>
//                             {getAge(patient.birhtday)} años
//                         </span>
//                     </div>
//                 </div>
//                 {/* Información de la cita */}
//                 <div style={styles.section}>
//                     <div style={styles.sectionTitle}>Cita</div>
//                     <div>
//                         <span style={styles.label}>Fecha:</span>{" "}
//                         <span style={styles.value}>
//                             {new Date(appointment.start).toLocaleString()}
//                         </span>
//                     </div>
//                     <div>
//                         <span style={styles.label}>ID Cita:</span>{" "}
//                         <span style={styles.value}>{appointment.id}</span>
//                     </div>
//                 </div>
//                 {/* Alergias */}
//                 {patient.anamnesis.length > 0 && (
//                     <div style={styles.section}>
//                         <div style={styles.sectionTitle}>Alergias</div>
//                         <div style={styles.boxSection}>
//                             <div>
//                                 <span style={styles.label}>Medicamentos:</span>{" "}
//                                 {patient.anamnesis[0].medicine_allergy}
//                             </div>
//                             <div>
//                                 <span style={styles.label}>Otros:</span>{" "}
//                                 {patient.anamnesis[0].other_allergy}
//                             </div>
//                             <div>
//                                 <span style={styles.label}>Material dental:</span>{" "}
//                                 {patient.anamnesis[0].dental_material_allergy}
//                             </div>
//                         </div>
//                     </div>
//                 )}
//                 {/* Hábitos */}
//                 {patient.habit.length > 0 && (
//                     <div style={styles.section}>
//                         <div style={styles.sectionTitle}>Hábitos</div>
//                         <div style={styles.boxSection}>
//                             {getHabits(patient.habit[0])}
//                         </div>
//                     </div>
//                 )}
//             </>
//         )}
//         {/* Contenido de Documentos */}
//         {activeTab === "documentos" && (
//             <>
//                 {patient.complementary_exams && patient.complementary_exams.length > 0 ? (
//                     <div style={{
//                         display: 'grid',
//                         gridTemplateColumns: 'repeat(2, 1fr)',
//                         gridAutoRows: 'auto',
//                         gap: 8,
//                     }}>
//                         {patient.complementary_exams.map((exam) => (
//                             <DocumentCard
//                                 key={exam.id}
//                                 exam={exam}
//                                 onImageClick={handleImageClick}
//                                 backendUrl={tegoServer.urls.odonto}
//                             />
//                         ))}
//                     </div>
//                 ) : (
//                     <div style={styles.noData}>
//                         No hay documentos disponibles
//                     </div>
//                 )}
//             </>
//         )}
//     </Container>
// </Dialog>
